"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: Object.getOwnPropertyDescriptor(all, name).get
    });
}
_export(exports, {
    get EMRA_ATTACHMENT_KINDS () {
        return EMRA_ATTACHMENT_KINDS;
    },
    get EMRA_CHECKSUM_PATTERN () {
        return EMRA_CHECKSUM_PATTERN;
    },
    get EMRA_PATIENT_NAME_MAX_LENGTH () {
        return EMRA_PATIENT_NAME_MAX_LENGTH;
    },
    get EMRA_RESULT_MAX_ATTACHMENTS () {
        return EMRA_RESULT_MAX_ATTACHMENTS;
    },
    get EMRA_RESULT_SUMMARY_MAX_LENGTH () {
        return EMRA_RESULT_SUMMARY_MAX_LENGTH;
    },
    get EMRA_TEST_NAME_MAX_LENGTH () {
        return EMRA_TEST_NAME_MAX_LENGTH;
    },
    get EmraChecksumSchema () {
        return EmraChecksumSchema;
    },
    get EmraLabResultAttachmentV1Schema () {
        return EmraLabResultAttachmentV1Schema;
    },
    get EmraLabResultResponseV1Schema () {
        return EmraLabResultResponseV1Schema;
    },
    get EmraLabResultV1Schema () {
        return EmraLabResultV1Schema;
    },
    get EmraMatchKeysV1Schema () {
        return EmraMatchKeysV1Schema;
    },
    get EmraMatchRefSchema () {
        return EmraMatchRefSchema;
    },
    get EmraOemPatientRefSchema () {
        return EmraOemPatientRefSchema;
    },
    get EmraPatientDetailsV1Schema () {
        return EmraPatientDetailsV1Schema;
    },
    get EmraResultIdSchema () {
        return EmraResultIdSchema;
    },
    get EmraUtcDateTimeSchema () {
        return EmraUtcDateTimeSchema;
    },
    get SourceOemSchema () {
        return SourceOemSchema;
    }
});
const _zod = require("zod");
const _labresult = require("../lab-results/lab-result");
const _patientfields = require("../patients/patient-fields");
const _patientlist = require("../patients/patient-list");
const EMRA_CHECKSUM_PATTERN = /^[0-9a-f]{64}$/;
const EmraChecksumSchema = _zod.z.string().regex(EMRA_CHECKSUM_PATTERN);
const EmraUtcDateTimeSchema = _zod.z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/).refine((value)=>isRoundTripDate(value), {
    message: 'Must be a real UTC date.'
});
function isRoundTripDate(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return false;
    }
    return date.toISOString() === value;
}
/**
 * An id EMRA issues: a lower-case UUID with version digit 1–8 and the RFC
 * variant, so the nil UUID is rejected (ADR-0018 §9).
 */ const EMRA_ISSUED_UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const EmraResultIdSchema = _zod.z.string().regex(EMRA_ISSUED_UUID_PATTERN);
/** Characters of an opaque matching reference: printable ASCII, no space. */ const MATCH_REF_BODY = '[\\x21-\\x7E]{1,64}';
const EmraMatchRefSchema = _zod.z.string().regex(new RegExp(`^${MATCH_REF_BODY}$`));
/** Body of a `sourceOem` slug, without anchors. */ const SOURCE_OEM_BODY = '[a-z0-9][a-z0-9_-]{0,31}';
const SourceOemSchema = _zod.z.string().regex(new RegExp(`^${SOURCE_OEM_BODY}$`));
const EmraOemPatientRefSchema = _zod.z.string().regex(new RegExp(`^${SOURCE_OEM_BODY}:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}:${MATCH_REF_BODY}$`));
const EmraMatchKeysV1Schema = _zod.z.object({
    philhealthPin: _patientlist.PhilHealthPinSchema.optional(),
    oemPatientRef: EmraOemPatientRefSchema.optional(),
    encounterRef: EmraMatchRefSchema.optional(),
    bookingNumber: EmraMatchRefSchema.optional()
}).strict();
function isControlCharacter(code) {
    return code <= 0x1f || code >= 0x7f && code <= 0x9f;
}
function hasControlCharacter(value, allowNewline) {
    for(let index = 0; index < value.length; index += 1){
        const code = value.charCodeAt(index);
        if (allowNewline && code === 0x0a) {
            continue;
        }
        if (isControlCharacter(code)) {
            return true;
        }
    }
    return false;
}
/**
 * Non-blank text of at most `max` characters with no leading or trailing
 * whitespace and no control characters (except `\n` when allowed).
 *
 * Untrimmed input is rejected rather than trimmed: parsing must not change
 * the value, or a checksum recomputed over the parsed body would differ
 * from the one the sender computed (ADR-0018 §9).
 */ function boundedText(max, options) {
    return _zod.z.string().min(1).max(max).refine((value)=>value.trim() === value, {
        message: 'Must not start or end with whitespace.'
    }).refine((value)=>value.trim().length > 0, {
        message: 'Must not be blank.'
    }).refine((value)=>!hasControlCharacter(value, options.allowNewline), {
        message: 'Must not contain control characters.'
    });
}
const EMRA_ATTACHMENT_KINDS = [
    'image',
    'audio',
    'video',
    'ecg',
    'other'
];
/**
 * A MIME type, `type/subtype`, lower case, no parameters (RFC 6838
 * restricted names, each part at most 64 characters). Lower case only, so
 * the value is one canonical string in the checksum.
 */ const CONTENT_TYPE_PATTERN = /^[a-z0-9][a-z0-9!#$&^_.+-]{0,63}\/[a-z0-9][a-z0-9!#$&^_.+-]{0,63}$/;
/**
 * A byte count as a decimal string: digits only, no sign, no leading zeros
 * except `"0"`. `canonicalJson` has no numbers (ADR-0018 §3). At most 15
 * digits, so `Number(sizeBytes)` is always exact.
 */ const SIZE_BYTES_PATTERN = /^(0|[1-9][0-9]{0,14})$/;
const EmraLabResultAttachmentV1Schema = _zod.z.object({
    attachmentId: _zod.z.string().regex(EMRA_ISSUED_UUID_PATTERN),
    kind: _zod.z.enum(EMRA_ATTACHMENT_KINDS),
    contentType: _zod.z.string().regex(CONTENT_TYPE_PATTERN),
    sizeBytes: _zod.z.string().regex(SIZE_BYTES_PATTERN),
    sha256: _zod.z.string().regex(EMRA_CHECKSUM_PATTERN)
}).strict();
const EMRA_PATIENT_NAME_MAX_LENGTH = 200;
const EmraPatientDetailsV1Schema = _zod.z.object({
    name: boundedText(EMRA_PATIENT_NAME_MAX_LENGTH, {
        allowNewline: false
    }).nullable(),
    birthDate: _patientfields.birthDate.nullable(),
    sex: _zod.z.enum([
        'F',
        'M'
    ]).nullable()
}).strict();
const EMRA_TEST_NAME_MAX_LENGTH = 200;
const EMRA_RESULT_SUMMARY_MAX_LENGTH = 4000;
const EMRA_RESULT_MAX_ATTACHMENTS = 64;
const EmraLabResultV1Schema = _zod.z.object({
    version: _zod.z.literal('1'),
    emraResultId: EmraResultIdSchema,
    matchKeys: EmraMatchKeysV1Schema,
    testName: boundedText(EMRA_TEST_NAME_MAX_LENGTH, {
        allowNewline: false
    }),
    resultSummary: boundedText(EMRA_RESULT_SUMMARY_MAX_LENGTH, {
        allowNewline: true
    }),
    abnormalFlag: _labresult.LabResultFlagSchema,
    resultedAt: EmraUtcDateTimeSchema.nullable(),
    sourceOem: SourceOemSchema,
    attachments: _zod.z.array(EmraLabResultAttachmentV1Schema).max(EMRA_RESULT_MAX_ATTACHMENTS),
    patientDetails: EmraPatientDetailsV1Schema,
    checksum: EmraChecksumSchema
}).strict().superRefine((value, ctx)=>{
    // Fixed messages only: never echo a value, since every one here is PHI.
    const ref = value.matchKeys.oemPatientRef;
    if (ref !== undefined && !ref.startsWith(`${value.sourceOem}:`)) {
        ctx.addIssue({
            code: 'custom',
            path: [
                'matchKeys',
                'oemPatientRef'
            ],
            message: 'Must start with the result sourceOem.'
        });
    }
    const seen = new Set();
    value.attachments.forEach((item, index)=>{
        if (seen.has(item.attachmentId)) {
            ctx.addIssue({
                code: 'custom',
                path: [
                    'attachments',
                    index,
                    'attachmentId'
                ],
                message: 'Must be unique within the result.'
            });
        }
        seen.add(item.attachmentId);
    });
});
const EmraLabResultResponseV1Schema = EmraLabResultV1Schema;
